import React, { useState, useEffect } from 'react';
import { getApiClient } from '../../api/apiClient';

const VinChecker = () => {
  const [vinChecks, setVinChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [paymentFilter, setPaymentFilter] = useState('all'); // 'all', 'paid', 'unpaid'
  const [vehicleInfoFilter, setVehicleInfoFilter] = useState('all'); // 'all', 'with-info', 'without-info'

  // Fetch VIN checks from API
  const fetchVinChecks = async () => {
    try {
      setRefreshing(true);
      const client = getApiClient();
      const response = await client.get('/admin/vinChecks');

      // Reverse the array to show most recent first
      setVinChecks(response.data.reverse());
      setError('');
    } catch (err) {
      console.error('Error fetching VIN checks:', err);
      setError('Failed to fetch VIN checks. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchVinChecks();
  }, []);

  // Open VIN checker in new tab
  const openVinChecker = (sessionId) => {
    const url = `https://servicegest.ro/vinChecker?session_id=${sessionId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle refund confirmation
  const handleRefundClick = (check) => {
    setSelectedRefund(check);
    setShowRefundModal(true);
  };

  const handleRefundConfirm = async () => {
    try {
      // TODO: Implement refund API call
      console.log('Refunding session:', selectedRefund.sessionId);
      // const token = await auth.currentUser?.getIdToken();
      // await axios.post(`https://api.servicegest.ro/admin/refund/${selectedRefund.sessionId}`, {}, {
      //   headers: { 'Authorization': `Bearer ${token}` }
      // });
      
      setShowRefundModal(false);
      setSelectedRefund(null);
      // fetchVinChecks(); // Refresh data after refund
    } catch (err) {
      console.error('Error processing refund:', err);
      setError('Failed to process refund. Please try again.');
    }
  };

  const handleRefundCancel = () => {
    setShowRefundModal(false);
    setSelectedRefund(null);
  };

  // Filter VIN checks based on selected filters
  const filteredVinChecks = vinChecks.filter(check => {
    const paymentMatch = paymentFilter === 'all' || 
      (paymentFilter === 'paid' && check.paid) ||
      (paymentFilter === 'unpaid' && !check.paid);
    
    const vehicleInfoMatch = vehicleInfoFilter === 'all' ||
      (vehicleInfoFilter === 'with-info' && check.vehicleInfo) ||
      (vehicleInfoFilter === 'without-info' && !check.vehicleInfo);
    
    return paymentMatch && vehicleInfoMatch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <svg className="animate-spin h-12 w-12 text-purple-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="mt-4 text-lg text-gray-600">Loading VIN checks...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
      
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards - horizontally scrollable on mobile */}
        <div className="mb-8 overflow-x-auto pb-2">
          <div className="flex gap-4 min-w-max md:grid md:grid-cols-2 lg:grid-cols-4 md:min-w-0">
          <div className="bg-white overflow-hidden shadow rounded-lg w-48 flex-shrink-0 md:w-auto">
            <div className="p-4 md:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">
                      {(paymentFilter !== 'all' || vehicleInfoFilter !== 'all') ? 'Filtered Sessions' : 'Total Sessions'}
                    </dt>
                    <dd className="text-base md:text-lg font-medium text-gray-900">{filteredVinChecks.length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg w-48 flex-shrink-0 md:w-auto">
            <div className="p-4 md:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">Paid Sessions</dt>
                    <dd className="text-base md:text-lg font-medium text-gray-900">
                      {filteredVinChecks.filter(check => check.paid).length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg w-48 flex-shrink-0 md:w-auto">
            <div className="p-4 md:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">With Vehicle Info</dt>
                    <dd className="text-base md:text-lg font-medium text-gray-900">
                      {filteredVinChecks.filter(check => check.vehicleInfo).length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg w-48 flex-shrink-0 md:w-auto">
            <div className="p-4 md:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">Unique Users</dt>
                    <dd className="text-base md:text-lg font-medium text-gray-900">
                      {new Set(filteredVinChecks.map(check => check.email)).size}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* VIN Checks Table (hidden on mobile) */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md hidden md:block">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">VIN Check Sessions</h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Recent VIN check sessions with payment and vehicle information status
                </p>
              </div>
              {(paymentFilter !== 'all' || vehicleInfoFilter !== 'all') && (
                <button
                  onClick={() => {
                    setPaymentFilter('all');
                    setVehicleInfoFilter('all');
                  }}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                >
                  Clear Filters
                </button>
              )}
            </div>
            
            {/* Filter Controls in Header */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="payment-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Status
                </label>
                <select
                  id="payment-filter"
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                  className="block w-full border pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 rounded-md"
                >
                  <option value="all">All</option>
                  <option value="paid">Paid Only</option>
                  <option value="unpaid">Unpaid Only</option>
                </select>
              </div>
              <div>
                <label htmlFor="vehicle-info-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Vehicle Information
                </label>
                <select
                  id="vehicle-info-filter"
                  value={vehicleInfoFilter}
                  onChange={(e) => setVehicleInfoFilter(e.target.value)}
                  className="block w-full border pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 rounded-md"
                >
                  <option value="all">All</option>
                  <option value="with-info">With Vehicle Info</option>
                  <option value="without-info">Without Vehicle Info</option>
                </select>
              </div>
            </div>
            
            {(paymentFilter !== 'all' || vehicleInfoFilter !== 'all') && (
              <div className="mt-3">
                <span className="text-sm text-gray-600">
                  Showing {filteredVinChecks.length} of {vinChecks.length} sessions
                </span>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    VIN Number
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact Info
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Session ID
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredVinChecks.map((check, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono font-medium text-gray-900">
                        {check.vin}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{check.email}</div>
                      <div className="text-sm text-gray-500">{check.phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          check.paid 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {check.paid ? 'Paid' : 'Unpaid'}
                        </span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          check.vehicleInfo 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {check.vehicleInfo ? 'Vehicle Info Available' : 'No Vehicle Info'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono text-gray-500 break-all select-all">
                        {check.sessionId}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex flex-col items-end gap-2">
                      <button
                        onClick={() => openVinChecker(check.sessionId)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 mb-1"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Open VIN Checker
                      </button>
                      {check.paid && (
                        <button
                          className="inline-flex items-center px-3 py-2 border border-red-200 text-sm leading-4 font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-400"
                          onClick={() => handleRefundClick(check)}
                        >
                          Refund
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Card Layout (only on mobile) */}
        <div className="md:hidden">
          {/* Mobile Filter Controls */}
          <div className="bg-white shadow rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-900">Filters</h3>
              {(paymentFilter !== 'all' || vehicleInfoFilter !== 'all') && (
                <button
                  onClick={() => {
                    setPaymentFilter('all');
                    setVehicleInfoFilter('all');
                  }}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label htmlFor="mobile-payment-filter" className="block text-xs font-medium text-gray-700 mb-1">
                  Payment Status
                </label>
                <select
                  id="mobile-payment-filter"
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                  className="block w-full pl-3 pr-8 py-2 text-sm border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 rounded-md"
                >
                  <option value="all">All</option>
                  <option value="paid">Paid Only</option>
                  <option value="unpaid">Unpaid Only</option>
                </select>
              </div>
              <div>
                <label htmlFor="mobile-vehicle-info-filter" className="block text-xs font-medium text-gray-700 mb-1">
                  Vehicle Information
                </label>
                <select
                  id="mobile-vehicle-info-filter"
                  value={vehicleInfoFilter}
                  onChange={(e) => setVehicleInfoFilter(e.target.value)}
                  className="block w-full pl-3 pr-8 py-2 text-sm border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 rounded-md"
                >
                  <option value="all">All</option>
                  <option value="with-info">With Vehicle Info</option>
                  <option value="without-info">Without Vehicle Info</option>
                </select>
              </div>
            </div>
            {(paymentFilter !== 'all' || vehicleInfoFilter !== 'all') && (
              <div className="mt-3">
                <span className="text-xs text-gray-600">
                  Showing {filteredVinChecks.length} of {vinChecks.length} sessions
                </span>
              </div>
            )}
          </div>

          <div className="space-y-4">
            {filteredVinChecks.map((check, index) => (
              <div key={index} className="bg-white rounded-lg shadow p-4 flex flex-col space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500">VIN</span>
                  <span className="font-mono text-sm text-gray-900">{check.vin}</span>
                </div>
                <div>
                  <span className="text-xs font-semibold text-gray-500">Contact</span>
                  <div className="text-sm text-gray-900">{check.email}</div>
                  <div className="text-sm text-gray-500">{check.phone}</div>
                </div>
                <div className="flex space-x-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    check.paid 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {check.paid ? 'Paid' : 'Unpaid'}
                  </span>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    check.vehicleInfo 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {check.vehicleInfo ? 'Vehicle Info Available' : 'No Vehicle Info'}
                  </span>
                </div>
                <div>
                  <span className="text-xs font-semibold text-gray-500">Session ID</span>
                  <div className="text-xs font-mono text-gray-500 break-all select-all">{check.sessionId}</div>
                </div>
                <div className="flex flex-col space-y-2">
                  <button
                    onClick={() => openVinChecker(check.sessionId)}
                    className="inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 w-full"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    Open VIN Checker
                  </button>
                  {check.paid && (
                    <button
                      className="inline-flex items-center justify-center px-3 py-2 border border-red-200 text-sm leading-4 font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-400 w-full"
                      onClick={() => handleRefundClick(check)}
                    >
                      Refund
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Information Panel */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">About VIN Checker Sessions</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  This page displays all VIN checker sessions from your platform. You can see payment status, 
                  vehicle information availability, and contact details. Click "Open VIN Checker" to view the 
                  session details in a new tab.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Refund Confirmation Modal */}
        {showRefundModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mt-2">Confirm Refund</h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to refund this VIN check session?
                  </p>
                  {selectedRefund && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <div className="text-xs text-gray-500 mb-1">VIN:</div>
                      <div className="font-mono text-sm text-gray-900 mb-2">{selectedRefund.vin}</div>
                      <div className="text-xs text-gray-500 mb-1">Email:</div>
                      <div className="text-sm text-gray-900">{selectedRefund.email}</div>
                    </div>
                  )}
                  <p className="text-xs text-red-600 mt-3">
                    This action cannot be undone.
                  </p>
                </div>
                <div className="flex justify-center space-x-3 mt-4">
                  <button
                    onClick={handleRefundCancel}
                    className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRefundConfirm}
                    className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    Confirm Refund
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VinChecker;
